import { CheckIcon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { CTA_GHOST, LINK_MUTED } from "../ui/theme";
import RailShell from "../ui/RailShell";
import { useOnboardingStore } from "../useOnboardingStore";
import { RAIL_COMPLETION, RAIL_STEPS } from "./steps";

/**
 * The instruction panel for a trialist's guided run.
 *
 * Chrome, sizing and z-order live in RailShell, shared with the logged-out
 * demo's GuideRail — this file owns only what differs: the copy, which corner
 * to sit in, and what the buttons do.
 */

/** Already standing on the page a step wants to send them to. */
function alreadyThere(to: string | undefined, pathname: string): boolean {
  if (!to) return false;
  if (to === "/dashboard") {
    return pathname === "/" || pathname.startsWith("/dashboard");
  }
  return pathname.startsWith(to.split("?")[0]);
}

export default function OnboardingRail() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const rail = useOnboardingStore((s) => s.state?.rail);
  const advanceRailStep = useOnboardingStore((s) => s.advanceRailStep);
  const exitRail = useOnboardingStore((s) => s.exitRail);

  if (!rail) return null;

  const total = rail.stepIds.length;
  const done = !!rail.completedAt;
  const stepId = rail.stepIds[rail.step];
  const step = RAIL_STEPS.find((s) => s.id === stepId);

  if (!done && !step) return null;

  // A step with somewhere to be offers to take them there — but only if they
  // are not already standing on it, where "Open Watchlists" is noise and the
  // button has nowhere to send them.
  const action =
    step?.action && !alreadyThere(step.action.to, pathname)
      ? step.action
      : undefined;

  /**
   * Which corner keeps out of the way of what is being asked for.
   *
   * The dashboard's headlines panels are leftmost in every template, and the
   * squawk button sits top-right in the header or in the left sidebar, so the
   * right-hand column below the header is the cheapest thing to cover. On the
   * builder the two start cards are top-left and top-centre, so the rail drops
   * to the bottom rather than landing on the card it just named.
   *
   * top-[4.25rem] clears the 40px header and 28px tab bar.
   *
   * The chat step is the exception: Tawk lives bottom-right, so a rail sitting
   * bottom-right on the builder would cover the bubble it is pointing at.
   */
  const onBuilder = pathname.startsWith("/watchlists-filters");
  const corner =
    onBuilder && stepId !== "chat" ? "bottom-6 right-5" : "right-5 top-[4.25rem]";

  return (
    <RailShell
      eyebrow="Getting started"
      stepIndex={rail.step}
      stepTotal={total}
      showProgress={!done}
      corner={corner}
      onClose={exitRail}
      closeLabel="Close getting started"
      closeAnalyticsId="onboarding-rail-close"
      footer={
        !done && (
          <>
            <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
              <CheckIcon className="h-3.5 w-3.5" />
              Continues when you do it
            </span>
            <button
              type="button"
              onClick={() => advanceRailStep({ skipped: true })}
              className={LINK_MUTED}
              data-analytics-id={`onboarding-rail-skip-${stepId}`}
            >
              Skip
            </button>
          </>
        )
      }
    >
      <div key={done ? "done" : stepId} className="nq-onb-rise flex flex-1 flex-col">
        {done ? (
          <>
            <p className="text-2xl font-semibold leading-snug text-gray-100 short-screen:text-xl">
              {RAIL_COMPLETION.headline}
            </p>
            <p className="mt-3 text-base leading-relaxed text-gray-300 short-screen:mt-2 short-screen:text-sm">
              {RAIL_COMPLETION.body}
            </p>
          </>
        ) : (
          <>
            <p className="text-2xl font-semibold leading-snug text-gray-100 short-screen:text-xl">
              {step!.instruction}
            </p>
            {step!.hint && (
              <p className="mt-3 text-base leading-relaxed text-gray-300 short-screen:mt-2 short-screen:text-sm">
                {step!.hint}
              </p>
            )}
            {action && (
              <button
                type="button"
                onClick={() => navigate(action.to)}
                className={`${CTA_GHOST} mt-auto w-full py-3.5 text-base`}
                // Navigation, not completion — this button does not advance the
                // run, because arriving on a page is not doing the thing on it.
                // The id keeps that distinction legible in a click report.
                data-analytics-id={`onboarding-rail-goto-${stepId}`}
              >
                {action.label}
              </button>
            )}
          </>
        )}
      </div>
    </RailShell>
  );
}
